import React, {useState} from 'react';
import {Checkbox, FlexColumn, Label, Modal, Text, TextField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import Util from '../../shared/util/util';
import UpdateFirmPortalSettingsMutation from '../../../mutations/update_firm_portal_settings_mutation';
import {showChangesSavedToast} from '../../shared/components/toasts/toast';

/**
 * Global Settings → Client Portal tab. Firm-wide portal settings: subdomain
 * (read-only until routing is live), default feature-flag values for new client
 * orgs, notification email infra (sender domain + reply-to), firm-wide phase-status
 * flag. Branding is at the client org level only. Reads viewer.firmPortalSettings
 * and saves through the portal proxy (no forecast_db write).
 */
type FirmPortalSettings = {
	portalSubdomain: string | null;
	portalCustomDomain: string | null;
	portalSenderDomain: string | null;
	portalReplyTo: string | null;
	defaultShowInvoices: boolean;
	defaultAllowFileUpload: boolean;
	phaseStatusEnabled: boolean;
};

type ClientPortalSettingsProps = {
	viewer: {firmPortalSettings: FirmPortalSettings | null};
};

export const ClientPortalSettings = ({viewer}: ClientPortalSettingsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const s = viewer?.firmPortalSettings ?? null;

	const [senderDomain, setSenderDomain] = useState<string>(s?.portalSenderDomain || '');
	const [replyTo, setReplyTo] = useState<string>(s?.portalReplyTo || '');
	const [defaultShowInvoices, setDefaultShowInvoices] = useState<boolean>(!!s?.defaultShowInvoices);
	const [defaultAllowFileUpload, setDefaultAllowFileUpload] = useState<boolean>(!!s?.defaultAllowFileUpload);
	const [phaseStatusEnabled, setPhaseStatusEnabled] = useState<boolean>(
		s?.phaseStatusEnabled == null ? true : !!s.phaseStatusEnabled
	);

	const onSave = () => {
		Util.CommitMutation(
			UpdateFirmPortalSettingsMutation,
			{
				portalSenderDomain: senderDomain || null,
				portalReplyTo: replyTo || null,
				defaultShowInvoices,
				defaultAllowFileUpload,
				phaseStatusEnabled,
			},
			() => showChangesSavedToast(formatMessage)
		);
	};

	return (
		<FlexColumn gap={'l'} data-cy="client-portal-settings">
			<Text size="2">
				<FormattedMessage id="client_portal.global_settings_title" defaultMessage="Client Workspace" />
			</Text>

			<TextField
				label={formatMessage({id: 'client_portal.subdomain', defaultMessage: 'Workspace subdomain'})}
				value={s?.portalSubdomain || ''}
				disabled
				helperText={formatMessage({
					id: 'client_portal.subdomain_coming_soon',
					defaultMessage: 'Subdomain routing is coming soon — this field is read-only for now.',
				})}
				size="large"
				width="flex"
			/>
			<TextField
				label={formatMessage({id: 'client_portal.sender_domain', defaultMessage: 'Email sender domain'})}
				value={senderDomain}
				onValueChange={setSenderDomain}
				size="large"
				width="flex"
			/>
			<TextField
				label={formatMessage({id: 'client_portal.reply_to', defaultMessage: 'Reply-to address'})}
				value={replyTo}
				onValueChange={setReplyTo}
				size="large"
				width="flex"
			/>

			<Label>
				<FormattedMessage id="client_portal.default_flags" defaultMessage="Defaults for new client orgs" />
			</Label>
			<Checkbox
				checked={defaultShowInvoices}
				onChange={() => setDefaultShowInvoices(p => !p)}
				labelText={formatMessage({id: 'client_portal.flag_show_invoices', defaultMessage: 'Show invoices'})}
			/>
			<Checkbox
				checked={defaultAllowFileUpload}
				onChange={() => setDefaultAllowFileUpload(p => !p)}
				labelText={formatMessage({id: 'client_portal.flag_allow_file_upload', defaultMessage: 'Allow file upload'})}
			/>
			<Checkbox
				checked={phaseStatusEnabled}
				onChange={() => setPhaseStatusEnabled(p => !p)}
				labelText={formatMessage({
					id: 'client_portal.phase_status_enabled',
					defaultMessage: 'Phase status enabled (firm-wide)',
				})}
			/>

			<Modal.PrimaryFooterButton onPress={onSave}>
				<FormattedMessage id="common.save" defaultMessage="Save" />
			</Modal.PrimaryFooterButton>
		</FlexColumn>
	);
};

export const ClientPortalSettingsQuery = graphql`
	query ClientPortalSettings_Query {
		viewer {
			actualPersonId
			component(name: "client_portal_settings")
			firmPortalSettings {
				portalSubdomain
				portalCustomDomain
				portalSenderDomain
				portalReplyTo
				defaultShowInvoices
				defaultAllowFileUpload
				phaseStatusEnabled
			}
		}
	}
`;

export default ClientPortalSettings;
